import React, { FC } from 'react';

import { Amounts } from 'src/types/common/Amounts';
import type { Location } from 'history';
import type { ImmutableTrip } from 'src/types/trips/Trips';
import type { DropdownOption } from 'src/common/components/DropdownDS22';
import { useSelector } from 'react-redux';
import { getTransaction } from 'src/common/utils/reduxStoreSelectors';
import {
  MOVED_PAGES,
  PATHS,
} from '../../../../common/constants';
import { preserveSearch } from '../../../../common/utils/routing';
import {
  ASK_TO_SEND_NEW_INQUIRY_STATES,
  RENTER_OFFER_EXPIRED_STATES,
} from '../../../constants';
import {
  CaptainCard,
  CommentCard,
  DatesCard,
  DurationCard,
  GroupSizeCard,
  TimeCard,
  VesselCard,
} from '../../cards';
import RenterPriceSection from '../../PriceSection/renter';
import TripPanel from '../../presentation/TripPanel';
import TripMenu from '../../TripMenu';
import TripDetailsTitle from '../../presentation/TripDetailsTitle';
import content, { getMessage } from '../content';
import TopScroll from '../../../../common/components/TopScroll';
import CTA, { CTALink } from '../../CTA';
import DeclineMessages from '../../DeclineMessages';

type BookingProps = {
  cancellationDropdownOptions: DropdownOption[];
  location: Location;
  trip: ImmutableTrip;
};

const Booking: FC<BookingProps> = ({
  cancellationDropdownOptions,
  location,
  trip,
}) => {
  const transaction = useSelector(getTransaction);
  const tripState = trip.getIn(['state', 'state']);
  const amounts: Amounts = transaction?.get('amounts')?.toJS()
  || trip?.getIn(['transaction', 'amounts'])?.toJS();
  const cta = (
    <CTA classNameModifier="withSidebar">
      <TripMenu
        cancellationDropdownOptions={cancellationDropdownOptions}
        trip={trip}
      />
      {ASK_TO_SEND_NEW_INQUIRY_STATES.includes(tripState) && (
        <CTALink
          to={`${MOVED_PAGES.LISTING}${trip.getIn(['boat', 'id'])}/?sheet=true`}
        >
          Send a new inquiry
        </CTALink>
      )}
    </CTA>
  );

  return (
    <TripPanel
      trip={trip}
      subheader={content.title[tripState] || trip.getIn(['state', 'badge'])}
      intro={getMessage(trip, location)}
      cta={cta}
      backLocation={preserveSearch(PATHS.INBOX, location)}
      classNameModifier="withoutSideMargins"
    >
      <TopScroll />
      <DeclineMessages trip={trip} />
      {RENTER_OFFER_EXPIRED_STATES.includes(tripState) && (
        <RenterPriceSection
          amounts={amounts}
          trip={trip}
        />
      )}

      <TripDetailsTitle />
      <VesselCard
        readOnly
        trip={trip}
      />
      <DatesCard
        readOnly
        trip={trip}
      />
      <TimeCard
        readOnly
        trip={trip}
      />
      <DurationCard
        trip={trip}
      />
      <GroupSizeCard
        readOnly
        trip={trip}
      />
      <CaptainCard
        readOnly
        trip={trip}
      />
      <CommentCard
        readOnly
        trip={trip}
      />
    </TripPanel>
  );
};

export default Booking;
